import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {useAuth} from './PhpAuthContext';
import {useHospitalAuth} from './HospitalAuthContext';
import {apiRequest, API_BASE_URL} from '../config/api';

const DataContext = createContext ();

export const useData = () => {
  const context = useContext (DataContext);
  if (!context) {
    throw new Error ('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({children}) => {
  const {profile} = useAuth ();
  const {hospitalUser, hospital} = useHospitalAuth ();
  const [submissions, setSubmissions] = useState ([]);
  const [doctors, setDoctors] = useState ([]);
  const [patients, setPatients] = useState ([]);
  const [loading, setLoading] = useState (true);

  const fetchDoctors = useCallback (async (addedByFilter = null) => {
    // Check both profile (regular auth) and hospitalUser (hospital auth)
    const currentUser = profile || hospitalUser;
    const userRole = profile?.role || (hospitalUser?.role === 'CHIEF_DOCTOR' ? 'chief_doctor' : 
                                       (hospitalUser?.role === 'CLINIC_ADMIN' ? 'admin' : hospitalUser?.role));
    
    if (!currentUser) {
      console.log('fetchDoctors: No profile or hospitalUser, skipping');
      return;
    }
    
    console.log('fetchDoctors: User role:', userRole, 'Profile:', profile, 'HospitalUser:', hospitalUser);
    
    // Only fetch doctors if user is admin, doctor, chief_doctor, or clinic_login
    if (!['admin', 'doctor', 'chief_doctor', 'clinic_login'].includes(userRole)) {
      console.log('fetchDoctors: Role not allowed, skipping. Role:', userRole);
      return;
    }
    
    setLoading (true);
    try {
      let url = 'get_users.php';
      const params = [];
      if (addedByFilter) {
        params.push(`added_by=${addedByFilter}`);
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      console.log('fetchDoctors: Fetching from URL:', url);
      const data = await apiRequest(url);
      console.log('fetchDoctors: API response:', data);
      if (data.success) {
        // Map users to doctor format for compatibility
        const doctorsData = data.users.map(user => ({
          id: user.id,
          name: user.full_name,
          username: user.username || '',
          email: user.email || '',
          phone: user.phone || '',
          sex: user.sex || '',
          date_of_birth: user.date_of_birth || '',
          status: user.status || 'Active',
          profile_photo: user.profile_photo || '',
          description: user.description || '',
          address: user.address || '',
          role: user.role || 'doctor',
          specialization: user.specialization || 'General Dentistry',
          experience: user.experience || '',
          rating: user.rating || '4.5',
          doctor_unique_id: user.doctor_unique_id || '',
          can_view_other_patients: user.can_view_other_patients || false,
          can_reset_password: user.can_reset_password || false,
          can_access_camp_submissions: user.can_access_camp_submissions || false,
          can_access_attendance: user.can_access_attendance || false,
          added_by_name: user.added_by_name || '',
          added_by_role: user.added_by_role || '',
          created_by: user.created_by || null
        }));
        console.log('fetchDoctors: Setting doctors:', doctorsData.length, 'items');
        setDoctors (doctorsData);
      }
    } catch (error) {
      console.error ('Error fetching doctors:', error.message);
    }
    setLoading (false);
  }, [profile, hospitalUser]);

  const fetchSubmissions = useCallback (
    async () => {
      // Check both profile (regular auth) and hospitalUser (hospital auth)
      const currentUser = profile || hospitalUser;
      if (!currentUser) {
        console.log('fetchSubmissions: No profile or hospitalUser, skipping');
        return;
      }
      
      // Normalize role - handle both profile.role and hospitalUser.role
      const userRole = profile?.role || (hospitalUser?.role === 'CHIEF_DOCTOR' ? 'chief_doctor' : 
                                         (hospitalUser?.role === 'CLINIC_ADMIN' ? 'admin' : 
                                          (hospitalUser?.role ? hospitalUser.role.toLowerCase() : null)));
      
      console.log('fetchSubmissions: User role:', userRole, 'Profile:', profile, 'HospitalUser:', hospitalUser);
      
      // Only fetch submissions if user is admin, doctor, chief_doctor, or clinic_login
      // Patients should use get_patient_own_records.php instead
      if (!userRole || !['admin', 'doctor', 'chief_doctor', 'clinic_login'].includes(userRole)) {
        console.log('fetchSubmissions: Role not allowed, setting empty array. Role:', userRole);
        setSubmissions([]);
        return;
      }
      
      setLoading (true);

      try {
        console.log('fetchSubmissions: Fetching submissions...');
        const data = await apiRequest('get_patient_record_submissions.php');
        console.log('fetchSubmissions: API response:', data);
        if (data.success) {
          console.log('fetchSubmissions: Success, setting submissions:', data.submissions?.length || 0, 'items');
          setSubmissions(data.submissions || []);
        } else {
          console.error('Failed to fetch submissions:', data.error);
          setSubmissions([]);
        }
      } catch (error) {
        console.error ('Error fetching submissions:', error.message);
        setSubmissions([]);
      }
      setLoading (false);
    },
    [profile, hospitalUser]
  );

  useEffect (
    () => {
      // Check both profile (regular auth) and hospitalUser (hospital auth)
      const currentUser = profile || hospitalUser;
      const userRole = profile?.role || (hospitalUser?.role === 'CHIEF_DOCTOR' ? 'chief_doctor' : 
                                         (hospitalUser?.role === 'CLINIC_ADMIN' ? 'admin' : hospitalUser?.role));
      
      // Only fetch doctors if user is admin, doctor, chief_doctor, or clinic_login
      if (currentUser && ['admin', 'doctor', 'chief_doctor', 'clinic_login'].includes(userRole)) {
        fetchDoctors ();
      } else {
        setDoctors([]);
      }
    },
    [profile, hospitalUser, fetchDoctors]
  );

  useEffect (
    () => {
      // Check both profile (regular auth) and hospitalUser (hospital auth)
      const currentUser = profile || hospitalUser;
      const userRole = profile?.role || (hospitalUser?.role === 'CHIEF_DOCTOR' ? 'chief_doctor' : 
                                         (hospitalUser?.role === 'CLINIC_ADMIN' ? 'admin' : 
                                          (hospitalUser?.role ? hospitalUser.role.toLowerCase() : null)));
      
      if (currentUser) {
        // Only fetch submissions if user is admin, doctor, chief_doctor, or clinic_login
        if (userRole && ['admin', 'doctor', 'chief_doctor', 'clinic_login'].includes(userRole)) {
          fetchSubmissions ();
        } else {
          setSubmissions([]);
        }
      } else {
        setSubmissions ([]);
      }
    },
    [profile, hospitalUser, fetchSubmissions]
  );

  const addSubmission = async submissionData => {
    // TODO: Implement PHP API endpoint for adding submissions
    try {
      // const data = await apiRequest('add_submission.php', {
      //   method: 'POST',
      //   body: JSON.stringify(submissionData),
      // });
      // setSubmissions(prev => [...prev, data.submission]);
      // return {data: data.submission};
      
      // Temporary: just add to local state
      const newSubmission = {
        ...submissionData,
        id: Date.now(),
        created_at: new Date().toISOString()
      };
      setSubmissions(prev => [...prev, newSubmission]);
      return {data: newSubmission};
    } catch (error) {
      console.error ('Error adding submission:', error.message);
      return {error};
    }
  };

  const updateSubmission = async (id, updates) => {
    // TODO: Implement PHP API endpoint for updating submissions
    try {
      // const data = await apiRequest('update_submission.php', {
      //   method: 'PUT',
      //   body: JSON.stringify({id, ...updates}),
      // });
      // setSubmissions(prev => prev.map(sub => sub.id === id ? data.submission : sub));
      // return {data: data.submission};
      
      // Temporary: just update local state
      setSubmissions(prev =>
        prev.map(sub => sub.id === id ? {...sub, ...updates} : sub)
      );
      return {data: {...updates, id}};
    } catch (error) {
      console.error ('Error updating submission:', error.message);
      return {error};
    }
  };

  const addDoctor = async (doctorData, viewAsAdmin = false) => {
    try {
      // Handle profile photo upload if present
      let profilePhotoUrl = null;
      if (doctorData.profile_photo) {
        try {
          const formData = new FormData();
          formData.append('photo', doctorData.profile_photo);
          
          const token = localStorage.getItem('session_token');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_photo.php`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            profilePhotoUrl = uploadData.url || uploadData.path || uploadData.filename;
          }
        } catch (uploadError) {
          console.warn('Photo upload failed, continuing without photo:', uploadError);
        }
      }
      
      // Get current user to check if they're a chief doctor
      const currentUser = profile || hospitalUser;
      const userRole = (profile?.role || hospitalUser?.role || '').toUpperCase();
      const roleLower = (profile?.role || hospitalUser?.role || '').toLowerCase();
      const isChiefDoctor = userRole === 'CHIEF_DOCTOR' || roleLower === 'chief_doctor';
      
      // Only pass view_as_admin if user is chief doctor and viewAsAdmin is true
      const shouldViewAsAdmin = viewAsAdmin && isChiefDoctor;
      
      const data = await apiRequest('add_user.php', {
        method: 'POST',
        body: JSON.stringify({
          name: doctorData.name,
          username: doctorData.username,
          password: doctorData.password || null, // Optional - backend will auto-generate if not provided
          email: doctorData.email || null,
          phone: doctorData.phone || '',
          sex: doctorData.sex || null,
          date_of_birth: doctorData.date_of_birth || null,
          status: doctorData.status || 'Active',
          profile_photo: profilePhotoUrl,
          description: doctorData.description || '',
          address: doctorData.address || '',
          role: doctorData.role || 'doctor',
          specialization: doctorData.specialization || 'General Dentistry',
          experience: doctorData.experience || '',
          rating: doctorData.rating || '4.5',
          view_as_admin: shouldViewAsAdmin
        }),
      });

      if (data.success && data.user) {
        // Map user to doctor format
        const newDoctor = {
          id: data.user.id,
          name: data.user.full_name,
          username: data.user.username || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          sex: data.user.sex || '',
          date_of_birth: data.user.date_of_birth || '',
          status: data.user.status || 'Active',
          profile_photo: data.user.profile_photo || '',
          description: data.user.description || '',
          address: data.user.address || '',
          role: data.user.role || 'doctor',
          specialization: data.user.specialization || 'General Dentistry',
          experience: data.user.experience || '',
          rating: data.user.rating || '4.5',
          doctor_unique_id: data.user.doctor_unique_id || '',
          can_view_other_patients: data.user.can_view_other_patients || false,
          can_reset_password: data.user.can_reset_password || false,
          can_access_camp_submissions: data.user.can_access_camp_submissions || false,
          can_access_attendance: data.user.can_access_attendance || false,
          added_by_name: data.user.added_by_name || '',
          added_by_role: data.user.added_by_role || '',
          created_by: data.user.created_by || null
        };
        setDoctors (prev => [...prev, newDoctor]);
        return {data: newDoctor};
      } else {
        // Better error message with details
        const errorMsg = data.message || data.error || 'Failed to add doctor';
        const details = [];
        if (data.sql_error) details.push(`SQL: ${data.sql_error}`);
        if (data.missing_fields) details.push(`Missing: ${data.missing_fields.join(', ')}`);
        if (data.received_data) details.push(`Received: ${data.received_data.join(', ')}`);
        const fullError = details.length > 0 ? `${errorMsg} (${details.join(' | ')})` : errorMsg;
        console.error('Add doctor error details:', data);
        throw new Error(fullError);
      }
    } catch (error) {
      console.error ('Error adding doctor:', error.message);
      return {error: {message: error.message}};
    }
  };

  const updateDoctor = async (id, updates) => {
    try {
      // Handle profile photo upload if it's a new file
      let profilePhotoUrl = updates.profile_photo;
      if (updates.profile_photo && updates.profile_photo instanceof File) {
        try {
          const formData = new FormData();
          formData.append('photo', updates.profile_photo);
          
          const token = localStorage.getItem('session_token');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_photo.php`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            profilePhotoUrl = uploadData.url || uploadData.path || uploadData.filename;
          }
        } catch (uploadError) {
          console.warn('Photo upload failed, continuing without photo:', uploadError);
        }
      }
      
      const data = await apiRequest('update_user.php', {
        method: 'PUT',
        body: JSON.stringify({
          id: id,
          name: updates.name,
          username: updates.username || null,
          password: updates.password || null, // Only update if provided
          phone: updates.phone,
          email: updates.email || null,
          sex: updates.sex || null,
          date_of_birth: updates.date_of_birth || null,
          status: updates.status || 'Active',
          profile_photo: profilePhotoUrl,
          description: updates.description || null,
          address: updates.address || null,
          role: updates.role || null,
          specialization: updates.specialization || null,
          experience: updates.experience || null,
          rating: updates.rating || null,
          can_view_other_patients: updates.can_view_other_patients !== undefined ? updates.can_view_other_patients : null,
          can_reset_password: updates.can_reset_password !== undefined ? updates.can_reset_password : null,
          can_access_camp_submissions: updates.can_access_camp_submissions !== undefined ? updates.can_access_camp_submissions : null,
          can_access_attendance: updates.can_access_attendance !== undefined ? updates.can_access_attendance : null
        }),
      });

      if (data.success && data.user) {
        // Map user to doctor format
        const updatedDoctor = {
          id: data.user.id,
          name: data.user.full_name,
          username: data.user.username || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          sex: data.user.sex || '',
          date_of_birth: data.user.date_of_birth || '',
          status: data.user.status || 'Active',
          profile_photo: data.user.profile_photo || '',
          description: data.user.description || '',
          address: data.user.address || '',
          role: data.user.role || 'doctor',
          specialization: data.user.specialization || 'General Dentistry',
          experience: data.user.experience || '',
          rating: data.user.rating || '4.5',
          doctor_unique_id: data.user.doctor_unique_id || '',
          can_view_other_patients: data.user.can_view_other_patients || false,
          can_reset_password: data.user.can_reset_password || false,
          can_access_camp_submissions: data.user.can_access_camp_submissions || false,
          can_access_attendance: data.user.can_access_attendance || false,
          added_by_name: data.user.added_by_name || '',
          added_by_role: data.user.added_by_role || '',
          created_by: data.user.created_by || null
        };
        setDoctors (prev => prev.map (doc => (doc.id === id ? updatedDoctor : doc)));
        return {data: updatedDoctor};
      } else {
        throw new Error(data.error || 'Failed to update doctor');
      }
    } catch (error) {
      console.error ('Error updating doctor:', error.message);
      return {error: {message: error.message}};
    }
  };

  const fetchPatients = useCallback (async (addedByFilter = null, viewAsAdmin = false) => {
    // Check both profile (regular auth) and hospitalUser (hospital auth)
    const currentUser = profile || hospitalUser;
    if (!currentUser) {
      console.log('fetchPatients: No profile or hospitalUser, skipping');
      return;
    }
    
    setLoading (true);
    try {
      let url = 'get_patients.php';
      const params = [];
      if (addedByFilter) {
        params.push(`created_by=${addedByFilter}`);
      }
      // Add view_as_admin parameter for chief_doctor viewing as admin
      // Check both profile.role and hospitalUser.role
      const userRole = profile?.role || hospitalUser?.role || '';
      const roleLower = userRole.toLowerCase();
      if (viewAsAdmin && (roleLower === 'chief_doctor' || userRole === 'CHIEF_DOCTOR')) {
        params.push('view_as_admin=true');
      }
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      const data = await apiRequest(url);
      if (data.success) {
        console.log('fetchPatients: Success, setting patients:', data.patients?.length || 0, 'items');
        setPatients (data.patients || []);
      } else {
        console.error('fetchPatients: API returned error:', data.error);
        setPatients([]);
      }
    } catch (error) {
      console.error ('Error fetching patients:', error.message);
      setPatients([]);
    }
    setLoading (false);
  }, [profile, hospitalUser]);

  // Fetch patients when profile or hospitalUser changes (separate from doctors to avoid conflicts)
  useEffect (
    () => {
      // Check both profile (regular auth) and hospitalUser (hospital auth)
      const currentUser = profile || hospitalUser;
      if (!currentUser) {
        setPatients([]);
        return;
      }
      
      // Check role from either profile or hospitalUser
      const userRole = (profile?.role || hospitalUser?.role || '').toUpperCase();
      const roleLower = (profile?.role || hospitalUser?.role || '').toLowerCase();
      
      // Allow admin, CLINIC_ADMIN, doctor, chief_doctor, CHIEF_DOCTOR, and clinic_login roles
      const allowedRoles = ['ADMIN', 'CLINIC_ADMIN', 'DOCTOR', 'CHIEF_DOCTOR', 'CLINIC_LOGIN'];
      const allowedRolesLower = ['admin', 'doctor', 'chief_doctor', 'clinic_login'];
      
      if (allowedRoles.includes(userRole) || allowedRolesLower.includes(roleLower)) {
        fetchPatients();
      } else {
        setPatients([]);
      }
    },
    [profile, hospitalUser, fetchPatients]
  );

  const addPatient = async patientData => {
    try {
      // Handle profile photo upload if present
      let profilePhotoUrl = null;
      if (patientData.profile_photo) {
        try {
          const formData = new FormData();
          formData.append('photo', patientData.profile_photo);
          
          const token = localStorage.getItem('session_token');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_photo.php`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            profilePhotoUrl = uploadData.url || uploadData.path || uploadData.filename;
          }
        } catch (uploadError) {
          console.warn('Photo upload failed, continuing without photo:', uploadError);
        }
      }
      
      const data = await apiRequest('add_patient.php', {
        method: 'POST',
        body: JSON.stringify({
          full_name: patientData.name, // Backend expects 'full_name'
          patient_unique_id: patientData.patient_unique_id || null,
          username: patientData.username,
          password: patientData.password,
          email: patientData.email || null,
          phone: patientData.phone || '',
          sex: patientData.sex || null,
          date_of_birth: patientData.date_of_birth || null,
          status: patientData.status || 'Active',
          profile_photo: profilePhotoUrl,
          description: patientData.description || '',
          address: patientData.address || '',
          branch: patientData.branch || null,
          is_child: patientData.is_child || false,
          parent_phone: patientData.parent_phone || null,
          parent_name: patientData.parent_name || null,
          parent_patient_id: patientData.parent_patient_id || null
        }),
      });

      if (data.success && data.patient) {
        const newPatient = {
          id: data.patient.id,
          patient_unique_id: data.patient.patient_unique_id,
          full_name: data.patient.full_name,
          username: data.patient.username || '',
          email: data.patient.email || '',
          phone: data.patient.phone || '',
          sex: data.patient.sex || '',
          date_of_birth: data.patient.date_of_birth || '',
          status: data.patient.status || 'Active',
          profile_photo: data.patient.profile_photo || '',
          description: data.patient.description || '',
          address: data.patient.address || '',
          branch: data.patient.branch || null,
          added_by_name: data.patient.added_by_name || '',
          added_by_role: data.patient.added_by_role || '',
          created_by: data.patient.created_by || null,
          created_at: data.patient.created_at || new Date().toISOString()
        };
        // Add patient to local state (it will be fetched again to ensure consistency)
        setPatients (prev => {
          // Check if patient already exists (avoid duplicates)
          const exists = prev.some(p => p.id === newPatient.id);
          if (exists) {
            return prev.map(p => p.id === newPatient.id ? newPatient : p);
          }
          return [newPatient, ...prev];
        });
        return {data: newPatient};
      } else {
        const errorMsg = data.message || data.error || 'Failed to add patient';
        const details = [];
        if (data.sql_error) details.push(`SQL: ${data.sql_error}`);
        if (data.missing_fields) details.push(`Missing: ${data.missing_fields.join(', ')}`);
        throw new Error(errorMsg + (details.length > 0 ? ' - ' + details.join(' | ') : ''));
      }
    } catch (error) {
      console.error ('Error adding patient:', error.message);
      return {error: {message: error.message}};
    }
  };

  const updatePatient = async (id, updates) => {
    try {
      // Handle profile photo upload if it's a new file
      let profilePhotoUrl = updates.profile_photo;
      if (updates.profile_photo && updates.profile_photo instanceof File) {
        try {
          const formData = new FormData();
          formData.append('photo', updates.profile_photo);
          
          const token = localStorage.getItem('session_token');
          
          const uploadResponse = await fetch(`${API_BASE_URL}/upload_photo.php`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            profilePhotoUrl = uploadData.url || uploadData.path || uploadData.filename;
          }
        } catch (uploadError) {
          console.warn('Photo upload failed, continuing without photo:', uploadError);
        }
      }
      
      const data = await apiRequest('update_patient.php', {
        method: 'PUT',
        body: JSON.stringify({
          id: id,
          is_child: updates.is_child || false,
          parent_phone: updates.parent_phone || null,
          parent_name: updates.parent_name || null,
          parent_patient_id: updates.parent_patient_id || null,
          name: updates.name,
          patient_unique_id: updates.patient_unique_id || null,
          username: updates.username || null,
          password: updates.password || null, // Only update if provided
          phone: updates.phone,
          email: updates.email || null,
          sex: updates.sex || null,
          date_of_birth: updates.date_of_birth || null,
          status: updates.status || 'Active',
          profile_photo: profilePhotoUrl,
          description: updates.description || null,
          address: updates.address || null,
          branch: updates.branch || null,
          can_reset_password: updates.can_reset_password !== undefined ? updates.can_reset_password : null
        }),
      });

      if (data.success && data.patient) {
        const updatedPatient = {
          id: data.patient.id,
          patient_unique_id: data.patient.patient_unique_id,
          full_name: data.patient.full_name,
          username: data.patient.username || '',
          email: data.patient.email || '',
          phone: data.patient.phone || '',
          sex: data.patient.sex || '',
          date_of_birth: data.patient.date_of_birth || '',
          status: data.patient.status || 'Active',
          profile_photo: data.patient.profile_photo || '',
          description: data.patient.description || '',
          address: data.patient.address || '',
          branch: data.patient.branch || null,
          can_reset_password: data.patient.can_reset_password || false,
          added_by_name: data.patient.added_by_name || '',
          added_by_role: data.patient.added_by_role || '',
          created_by: data.patient.created_by || null
        };
        setPatients (prev => prev.map (pat => (pat.id === id ? updatedPatient : pat)));
        return {data: updatedPatient};
      }
    } catch (error) {
      console.error ('Error updating patient:', error.message);
      return {error};
    }
  };

  const value = {
    submissions,
    doctors,
    patients,
    loading,
    addSubmission,
    updateSubmission,
    addDoctor,
    updateDoctor,
    addPatient,
    updatePatient,
    fetchSubmissions,
    fetchDoctors,
    fetchPatients,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
